import { TextDirection } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import { LocatorFactory } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { SelectorFactory } from "@colibrio/colibrio-reader-framework/colibrio-core-selector-base";
import { EpubCfiFragmentSelectorParser } from "@colibrio/colibrio-reader-framework/colibrio-core-selector-epubcfi";
import { PdfFragmentSelectorParser } from "@colibrio/colibrio-reader-framework/colibrio-core-selector-pdf";
import { ContainerContentBlockType, EngineEventTargetFrameworkComponent, ReaderDocumentEventState, ReaderViewGestureType } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { WebSpeechTtsSynthesizer } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine";
import { CirrusReaderNavigationType } from "../shared/CirrusReaderModel";
import type { IAttributeData, IIntegerRange } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import type {
    IReaderPublication, 
    IReaderViewEngineEvent, 
    IReaderViewGotoOptions, 
    IPointerEngineEvent, 
    INavigationIntentEngineEvent,
    IFocusOnReadingPositionOptions,
    IMouseEngineEvent,
    IEngineEventMediaResourceData,
    IEngineEventTargetXmlElementNodeData,
    ISelectionChangedEngineEvent,
    IVisibleContentRectData,
    IKeyboardEngineEvent
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type {
    ICirrusReaderContentPositionData, 
    ICirrusReaderNavigationAction, 
    ICirrusReaderReadingProgressionTimeline, 
    ICirrusReaderReadingProgressionTimelineData, 
    ICirrusReaderSyncMediaOptions, 
    ICirrusReaderUserProfileData
} from "../shared/CirrusReaderModel";
import type {
    ISimpleLocatorData, 
    ILocator, 
    ILocatorData
} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { useCirrusHighlighting } from "../composables/CirrusHighlighting";
import { MediaTypeCategory } from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import type { CirrusLandmarksCollection } from "../shared/CirrusReaderLandmarkCollection";
import { RendererType } from "~/configuration/types";
export interface ICoordinates {
    x: number;
    y: number;
}

export default defineNuxtPlugin({
    name: 'cirrus-colibrio-reader',
    dependsOn: [
        'cirrus-reader-reading-system',
        'cirrus-reader-publication',
        'cirrus-reader-view',
        'cirrus-reader-media-player',
        'cryptography',
    ],
    enforce: 'pre',
    async setup (_nuxtApp) {
        const { variant } = useAppVariant()
        if (variant.value !== 'reader') return {};

        console.info('[CirrusPlugin] CirrusReader.setup()');
        const { 
            $cirrusReaderView, 
            $cirrusReaderPublication, 
            $cirrusReaderReadingSystem,
            $cirrusReaderMediaPlayer,
        } = useNuxtApp();
        const cirrusReaderUiStore = useCirrusReaderUiStore();
        const uiBlockingGate = useUiBlockingGate();
        const cirrusReaderSettingsStore = useCirrusReaderSettingsStore();

        let readerIsLoaded: boolean = false;
        let cirrusReadingProgressionTimeLine: ICirrusReaderReadingProgressionTimeline | undefined;
        let webSpeechPlayerForTextSelections: WebSpeechTtsSynthesizer | undefined;

        let _colibrioReaderPublication: IReaderPublication | undefined;
        let _readerViewElement: HTMLElement | undefined;
        let _ttsVoice: SpeechSynthesisVoice | undefined;
        let _shouldFocusOnReadingPosition: boolean = true;
        let _contentSelectionHasBeenCleardDuringPointerEventLifecycle: boolean = false;
        let readerViewPointerUpTimeoutHandle: number | undefined;
        let readerViewPointerHoverTimeoutHandle: number | undefined;
        let latestReaderViewPointerDownEventCoords: ICoordinates | undefined;
        let latestReaderViewPointerUpEventCoords: ICoordinates | undefined;
        let latestReaderViewPointerDownEventButton: number | undefined;
        let latestReaderViewPointerEventPointerType: 'mouse' | 'pointer' | 'touch' | string = 'mouse';
        let preventMenuOpenOnNextPointerUp: boolean;
        let latestCaretLocatorData: ISimpleLocatorData | undefined;

        const initReader = (readerViewElement: HTMLElement | undefined, colibrioLicenseApiKey: string, _userProfileData: ICirrusReaderUserProfileData) => {
            console.log("CirrusReader.initReader");

            if (readerIsLoaded) $cirrusReaderReadingSystem.destroy();
            $cirrusReaderReadingSystem.initReadingSystem(colibrioLicenseApiKey, _userProfileData);
            _readerViewElement = readerViewElement;
            _setUpComponentCallbacks();
            readerIsLoaded = true;
        }

        const loadPublicationFromUrl = async (initialLocator?: string, user?: string, sessionToken?: string) => {
            try {
                _colibrioReaderPublication = await $cirrusReaderReadingSystem.loadPublicationFromUrl(user, sessionToken);
            } catch (reason){
                throw new DOMException('CirrusReader.loadPublicationFromUrl(), Failed to load publication. ' + reason);
            }
            await _onAfterPublicationLoaded(_colibrioReaderPublication, initialLocator);
        }

        cirrusReaderUiStore.$subscribe((_mutation, state) => {
            toggleMediaPlayer(state.readAloudOpen, {});
        });
        
        const _setUpComponentCallbacks = (): void => {

            $cirrusReaderView.onClick = (async (ev: IMouseEngineEvent) => {
                const mediaResourceData: IEngineEventMediaResourceData | null = ev.toJSON().mediaResource;
                if (mediaResourceData) {
                    _event_mediaResourceClicked(mediaResourceData);
                    ev.preventDefault();
                }

                if (useCirrusReaderUtils().eventDataIsMouseEngineEventData(ev.toJSON())) {

                    if (ev.toJSON().defaultPrevented || ev.toJSON().readerDocumentEventState === ReaderDocumentEventState.DEFAULT_PREVENTED) {
                        return;
                    }
                    const nodeData = ev.toJSON().target?.nodeData;
                    const isMediaElement = nodeData ? (nodeData?.nodeName === 'img' ||
                                                nodeData?.nodeName === 'video' ||
                                                nodeData?.nodeName === 'audio') : false;

                    if ($cirrusReaderReadingSystem.isInAccessibilityMode && !cirrusReaderUiStore.getPopoverState() && !isMediaElement) {
                        const targetSelector = ev.toJSON().target?.locator?.selectors[0];

                        if (targetSelector) {
                            useCirrusSharedAPI().navigateTo(`#${targetSelector}`, false);
                            ev.preventDefault();
                            ev.stopImmediatePropagation();

                        } else {
                            console.warn('No locator was available in the event data so we dont show the actions dialog.');
                        }
                    }
                }
            });
            
            $cirrusReaderView.onReadingPositionChanged = async (ev: IReaderViewEngineEvent) => {
                const readingPosition = ev.readerView.getReadingPosition();
                const readingPositionData = await $cirrusReaderView?.getReadingPositionData();

                if ($cirrusReaderView && readingPosition && readingPositionData && $cirrusReaderPublication && $cirrusReaderPublication.id) {
                    useCirrusReaderNavigation().setReadingPositionData(readingPositionData);
                    cirrusReadingProgressionTimeLine?.updateTimelinePosition(readingPosition.getLocator());
                }

                if (!useUiBlockingGate().isUiBlocking.value && cirrusReaderSettingsStore.activeRenderer !== RendererType.Enum.SingleDocumentScrollRenderer && !$cirrusReaderMediaPlayer.mediaPlayerIsPlaying) {
                    useFocusOnReadingPosition().focus();
                }
            };
    
            $cirrusReaderView.onPointerDown = (ev: IPointerEngineEvent) => {
                _event_pointerDown(ev);
                _contentSelectionHasBeenCleardDuringPointerEventLifecycle = false;
                preventMenuOpenOnNextPointerUp = false;

                latestReaderViewPointerDownEventCoords = {
                    x: ev.clientX,
                    y: ev.clientY,
                };
                latestReaderViewPointerDownEventButton = ev.buttons;
                latestReaderViewPointerEventPointerType = ev.pointerType;

                if(readerViewPointerHoverTimeoutHandle) {
                    clearTimeout(readerViewPointerHoverTimeoutHandle);
                }
            };


            $cirrusReaderView.onSelectionChanged = async (ev: ISelectionChangedEngineEvent, selectionRectData?: IVisibleContentRectData[] | undefined) => {
                const isMouseSelection = latestReaderViewPointerEventPointerType === 'mouse';
                const srcEventData = ev.toJSON();
                latestCaretLocatorData = srcEventData.locator || undefined;

                _contentSelectionHasBeenCleardDuringPointerEventLifecycle = cirrusReaderUiStore.getActiveSelection() !== null && !srcEventData.isRange;

                if (ev.isRange) {
                    $cirrusReaderView.colibrioReaderView.setAllowedGestureTypes([]);
                    cirrusReaderUiStore.setActiveSelection({
                        locatorData: srcEventData.locator,
                        selector: '#' + srcEventData.locator?.selectors[0],
                        isRange: srcEventData.isRange,
                        selectionText: srcEventData.selectionText || '',
                        selectionRectData: selectionRectData,
                        isMouseSelection: isMouseSelection,
                    });
                } else {
                    $cirrusReaderView.colibrioReaderView.setAllowedGestureTypes([
                        ReaderViewGestureType.SWIPE_NAVIGATION,
                        ReaderViewGestureType.PAN_ZOOM,
                    ]);
                    if(_contentSelectionHasBeenCleardDuringPointerEventLifecycle) {
                        preventMenuOpenOnNextPointerUp = true;
                    }
                    cirrusReaderUiStore.setActiveSelection(undefined);
                }

                if (!isMouseSelection || _contentSelectionHasBeenCleardDuringPointerEventLifecycle) {
                    //this._maybeToggleSelectionUiElements();
                }
                
                if ($cirrusReaderMediaPlayer.mediaPlayerIsPlaying && srcEventData.locator && !srcEventData.isRange) {
                    $cirrusReaderMediaPlayer.seekToLocatorWithinVisibleRange(srcEventData.locator);
                }
                
                const locator = ev.contentLocation?.getLocator().toString();
                const clientRectData = (locator) ? await $cirrusReaderView.fetchVisibleContentRectDataForContentLocator(locator) : []
                cirrusReaderUiStore.setPopoverState({
                    selectionText: ev.selectionText ?? "",
                    selectionLocatorData: ev.toJSON().locator || undefined,
                    selectionRect: clientRectData
                });
            }

            $cirrusReaderView.onPointerUp = (ev: IPointerEngineEvent) => {
                _event_pointerUp(ev);

                let internalLatestReaderViewPointerDownEventCoords: ICoordinates | undefined = latestReaderViewPointerDownEventCoords;
                let wasDoubleClick: boolean = false;

                if (readerViewPointerHoverTimeoutHandle) {
                    clearTimeout(readerViewPointerHoverTimeoutHandle);
                }

                if (readerViewPointerUpTimeoutHandle !== undefined){
                    ev.preventDefault();
                    wasDoubleClick = true;
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const _internalReaderViewPointerUp = (pointerUpEventCoords: any, pointerDownEventCoords: any, wasDoubleClick: any) => {
                    if (!wasDoubleClick) {
                        _maybeToggleMainMenu(ev, pointerUpEventCoords, pointerDownEventCoords);
                    }
                    readerViewPointerUpTimeoutHandle = undefined;
                }

                const internalLatestReaderViewPointerUpEventCoords: ICoordinates = {
                    x: ev.clientX,
                    y: ev.clientY,
                };

                latestReaderViewPointerUpEventCoords = internalLatestReaderViewPointerUpEventCoords;

                if (!internalLatestReaderViewPointerDownEventCoords) {
                    internalLatestReaderViewPointerDownEventCoords = internalLatestReaderViewPointerUpEventCoords;
                    latestReaderViewPointerDownEventCoords = latestReaderViewPointerUpEventCoords;
                }

                readerViewPointerUpTimeoutHandle = window.setTimeout(
                    _internalReaderViewPointerUp.bind(this, internalLatestReaderViewPointerUpEventCoords, internalLatestReaderViewPointerDownEventCoords, wasDoubleClick),
                    200
                );
            };

            $cirrusReaderView.onNavigationIntent = (ev: INavigationIntentEngineEvent) => {
                _event_navigationIntent(ev);
            };

            $cirrusReaderView.onKeyUp = (ev: IKeyboardEngineEvent) => {
                ev.preventDefault();
                ev.stopImmediatePropagation();
                const srcEventData = ev.toJSON();
                if(srcEventData.key === 'Enter' && srcEventData.target?.nodeData?.nodeName === 'img') {
                    //this._handleKeyUpOnImages(ev);
                    return;
                }
                if ($cirrusReaderView.colibrioReaderView.isNavigating() && (ev.key == "ArrowLeft" || ev.key == "ArrowRight")) return;
                useHandleKeyboardShortcut(srcEventData.code, srcEventData.key, srcEventData.modifiers.alt, srcEventData.modifiers.shift, srcEventData.modifiers.control);
            }
        }


        const initaliseWebSpeechPlayer = async (): Promise<WebSpeechTtsSynthesizer> => {
            console.log("CirrusReader.initaliseWebSpeechPlayer");

            webSpeechPlayerForTextSelections = new WebSpeechTtsSynthesizer((utterance: SpeechSynthesisUtterance) => {
                if (_ttsVoice) {
                    utterance.voice = _ttsVoice;
                }
            });

            webSpeechPlayerForTextSelections.init({
                onTtsSynthesizerWaiting: () => {

                },
                onUtteranceBoundary: (_utteranceId: number, _startOffset: number, _length: number | null) => {

                },
                onUtteranceEnd: (_utteranceId: number) => {
                    
                },
                onUtteranceError: (_utteranceId: number, _error: unknown) => {
                    
                },
                onUtterancePaused: (_utteranceId: number) => {
                    
                },
                onTtsSynthesizerReady: () => {

                }
            });
            return webSpeechPlayerForTextSelections;
        }

        const speakText = (text: string) => {
            const ttsVoiceDir = $cirrusReaderPublication.isRtl || true;
            console.log(`CirrusReader.speakText`);

            if (window.speechSynthesis.speaking) {
                window.speechSynthesis.cancel();
            }
            webSpeechPlayerForTextSelections?.addUtterance({
                text: {
                    lang: _ttsVoice ? _ttsVoice.lang.replace("_", "-") : "en",
                    value: text,
                    dir: ttsVoiceDir ? TextDirection.RTL : TextDirection.LTR
                },
                id: Date.now(),
                charOffset: 0,
                nodeData: null,
            });
            webSpeechPlayerForTextSelections?.play();
        }

        const _onAfterPublicationLoaded = async (
            colibrioReaderPublication: IReaderPublication,
            initialLocator?: string | undefined,
        ): Promise<void> => {
            console.log("CirrusReader._onAfterPublicationLoaded");

            await $cirrusReaderPublication.initPublication(colibrioReaderPublication);

            if (!$cirrusReaderPublication.isAudiobook) {
                if (_readerViewElement){
                    $cirrusReaderView.initReaderView(_readerViewElement!);
                    // Now we need to do some asynchronous set up, so we call the async _setup() method.
                    await $cirrusReaderView.initCirrusReaderView().catch(console.warn);
                    _createReadingProgressionTimeline().then((timeline)=>{
                        cirrusReadingProgressionTimeLine = timeline;
                    });
                } else {
                    console.error('CirrusReader._onAfterPublicationLoaded(), _readerViewElement is undefined');
                }
            }
            
            if (typeof initialLocator === 'undefined' || initialLocator == ""){
                goToStart().catch(console.error);
            } else {
                goTo(initialLocator).catch(() => {
                    goToStart().catch(console.error);
                });
            }

            initaliseWebSpeechPlayer();
            useCirrusReaderNavigation().setPublicationData(await $cirrusReaderPublication.serialize());
            useCirrusBookmarking().initBookmarksLayer();
            useCirrusHighlighting().initHighlightsLayer();
        }

        const _createReadingProgressionTimeline = async (
            timelineSerializedData?: string | undefined,
        ): Promise<ICirrusReaderReadingProgressionTimeline | undefined> => {
            console.log("CirrusReader._createReadingProgressionTimeline");

            const readingProgressionTimeLine: ICirrusReaderReadingProgressionTimeline | undefined = useCirrusProgressionTimeline();
    
            if (!$cirrusReaderPublication.isAudiobook && $cirrusReaderView) {
                await readingProgressionTimeLine.create!(timelineSerializedData, (_progress: number) => {});
            }
            
            $cirrusReaderPublication.progressionTimeline = readingProgressionTimeLine;
            cirrusReadingProgressionTimeLine = readingProgressionTimeLine;
    
            readingProgressionTimeLine?.onTimelineReady((_timelineData: ICirrusReaderReadingProgressionTimelineData) => {
                useCirrusReaderUiStore().setNavigationAvailable(true);
                _event_timelineReady();
            });
    
            return readingProgressionTimeLine;
        }

        const navigate = (
            navigationAction: ICirrusReaderNavigationAction
        ): void => {
            console.log("CirrusReader.navigate");

            switch (navigationAction.navigationType) {
                case CirrusReaderNavigationType.NEXT:
                    next().catch(console.warn);
                    break;
                case CirrusReaderNavigationType.PREVIOUS:
                    previous().catch(console.warn);
                    break;
                case CirrusReaderNavigationType.TIMELINEPOSITION:
                    if (navigationAction.position !== undefined) {
                        gotoTimelinePosition(navigationAction.position).catch(console.error);
                    } else {
                        console.warn('CirrusReader.navigate: event.detail.position is undefined');
                    }
                    break;
                case CirrusReaderNavigationType.GOTO:
                    if (navigationAction.locator) {
                        goTo(navigationAction.locator).catch(console.error);
                    } else {
                        console.warn('CirrusReader.navigate: event.detail.location is undefined');
                    }
                    break;
                case CirrusReaderNavigationType.GOTOSTART:
                    goToStart().catch(console.error);
                    break;
            }
        }


        /**
         * Event handlers
         */
        const _event_timelineReady = async () => {
            console.log("CirrusReader._event_timelineReady");

            if ($cirrusReaderPublication && $cirrusReaderPublication.progressionTimeline) {
                $cirrusReaderView?.readingStats?.setPublicationLength($cirrusReaderPublication.progressionTimeline.getLength());
                const readingPosition = $cirrusReaderView?.getReadingPosition();
                if (readingPosition){
                    cirrusReadingProgressionTimeLine?.updateTimelinePosition(readingPosition.getLocator());
                }
    
                await $cirrusReaderPublication?.buildNavigationTree($cirrusReaderPublication.progressionTimeline).catch(console.error);
                await $cirrusReaderPublication?.buildPageList($cirrusReaderPublication.progressionTimeline).catch(console.error);
    
                if ($cirrusReaderView && $cirrusReaderPublication.pageListData) {
                    //cirrusReaderView.createPageMarkerLayer($cirrusReaderPublication.pageListData);
                }
    
                // As we now have both timeline and navigation tree, we can send more complete visible range data to the UI.
                _event_visibleRangeDataChanged().catch(console.error);
            }
        };

        const _event_navigationInternalIntent = async (ev: INavigationIntentEngineEvent, __filenamecontentPositionData?: ICirrusReaderContentPositionData) => {

            if (ev.relatedEvent?.target) {
                const nodeData = ev.relatedEvent.target.nodeData;
                if (nodeData) {
                    if (ev && ev.locator) {
                        ev.preventDefault();
                        const locator = ev.locator.toString();
                        navigate({
                            navigationType: CirrusReaderNavigationType.GOTO,
                            locator: locator,
                            focusNearContentLocation: true
                        });
                    }

                } else if (ev && ev.locator) {
                    // Probably a PDF since there is no nodeData.
                    ev.preventDefault();
                    const locator = ev.locator.toString();
                    navigate({
                        navigationType: CirrusReaderNavigationType.GOTO,
                        locator: locator,
                        focusNearContentLocation: true
                    });
                }

            } else {
                console.warn('CirrusReader._event_navigationInternalIntent: got invalid locator.');
            }
        }

        const _event_navigationExternalIntent = async (targetUrl: string) => {
            // Do not open urls that are local to the "com.colibrio" scope.
            if (targetUrl && !targetUrl.includes('com.colibrio')) {
                useGlobalModal().openModal({
                    message: `Do you wish to open "${targetUrl}"?`,
                    buttonAction: () => {
                        window.open(targetUrl);
                    },
                    buttonActionText: "Yes",
                });
                
            }
        }

        const _event_visibleRangeDataChanged = async () => {
            console.log("CirrusReader._event_visibleRangeDataChanged");
        
            // Invalidate the current rects and update them based on the current visible content.
            await $cirrusReaderView?.updateAndFetchVisibleContentRects();
    
            const visibleRangeData = await $cirrusReaderView?.fetchVisibleRangeData($cirrusReaderPublication?.navigationTree, cirrusReadingProgressionTimeLine);
            if (visibleRangeData) {
                if (cirrusReadingProgressionTimeLine?.isReady() && visibleRangeData.locator) {
                    try {
                        const wordRange: IIntegerRange | undefined = await cirrusReadingProgressionTimeLine?.fetchTimelineRange(visibleRangeData.locator);
                        if (wordRange) {
                            _registerWordRangeReadToStats(new IntRange(wordRange.start, wordRange.end));
                        }
                    } catch (err) {
                        console.warn(`Unable to fetchTimelineRange()`, err);
                    }
                    useCirrusReaderNavigation().setVisibleRangeData(visibleRangeData);
                }
            }
        };

        const _event_mediaResourceClicked = async (mediaResourceData: IEngineEventMediaResourceData) => {
            if ($cirrusReaderPublication && !$cirrusReaderPublication.isFixedLayout) {
                let mediaElementAttributes: IAttributeData[] | undefined;

                if (mediaResourceData && mediaResourceData.resourceUrl && mediaResourceData.mediaTypeCategory === MediaTypeCategory.IMAGE) {
                    const mediaResourceBlobUrl = await $cirrusReaderPublication.fetchResourceBlobUrl(mediaResourceData.resourceUrl);
                    $cirrusReaderPublication.fetchLandmarkCollection().then((landmarkCollection: CirrusLandmarksCollection | undefined) => {

                        if (landmarkCollection && mediaResourceData?.resourceUrl) {
                            const mediaElementUrl = new URL(mediaResourceData.resourceUrl);
                            const mediaElement = landmarkCollection.getFigures()?.find((mediaElement) => {
                                if (!mediaElement || !mediaElement.resourceHrefs || mediaElement.resourceHrefs.length === 0) {
                                    return false;
                                }
                                return mediaElementUrl.pathname.includes(mediaElement.resourceHrefs[0] ?? "");
                            });
                            mediaElementAttributes = mediaElement ? mediaElement.attributes : undefined;
                        }

                        /*if (this.activeSelection || this._contentSelectionHasBeenCleardDuringPointerEventLifecycle) {
                            return ;
                        }

                        if (this.isReaderViewIsInPanZoomMode === true) {
                            return;
                        }*/

                        if (mediaResourceBlobUrl) {
                            let description: string | undefined;
                            if (mediaElementAttributes && mediaElementAttributes.length > 0) {
                                description = mediaElementAttributes.find(attribute => attribute.localName === 'alt')?.value;
                            }
                            showImageViewDialog(mediaResourceBlobUrl, description, true).catch(console.warn);
                        } else {
                            console.warn('_vanillaReaderEvent_mediaObjectViewIntent(): mediaUrl event data was undefined.');
                        }

                    }).catch(console.warn);
                }
            }
        }

        const showImageViewDialog = async (_src: string, _description?: string, _srcIsBlobUrl?: boolean) => {}
    

        const _maybeToggleMainMenu = (ev: IPointerEngineEvent, latestReaderViewPointerUpEventCoords: ICoordinates, latestReaderViewPointerDownEventCoords: ICoordinates): void => {

            let doNotShowMenu = preventMenuOpenOnNextPointerUp ? true :  !cirrusReaderUiStore.getActiveSelection()?.isRange || !latestCaretLocatorData  ? false : true;

            if (useCirrusReaderUtils().eventDataIsPointerEngineEventData(ev.toJSON())) {

                const hasMediaPlayerReadAloudAttribute = ev.target?.nodeData?.attributes.some((attribute: IAttributeData) => {
                    return attribute.localName === 'ibooks-readaloud' || attribute.localName === 'readaloud';
                });

                if (hasMediaPlayerReadAloudAttribute) {
                    _startMediaPlayback(ev.toJSON().target?.locator?.selectors[0] || undefined);
                }

                if (ev.mediaResource) {
                    // Clicks on images are handled in the `event_mediaObjectViewIntent` method, so we can return.
                    return;
                }

                const eventTarget = ev.target;

                const bubbleSearchForIgnoreElement = (nodeData: IEngineEventTargetXmlElementNodeData): boolean => {

                    const nodeName = nodeData.nodeName?.toLocaleLowerCase();

                    if(!nodeName || nodeName === 'body') {
                        return false;
                    }

                    // Filter out other elements where we should not interfere in the original event behaviour
                    switch (nodeName) {
                        case 'a':
                        case 'img':
                        case 'details':
                        case 'summary':
                        case 'button':
                        case 'select':
                        case 'input':
                        case 'textarea':
                            return true;

                        default:
                            if(nodeData.parent) {
                                return bubbleSearchForIgnoreElement(nodeData.parent);
                            } else {
                                return false;
                            }
                    }
                }

                if(ev.target?.nodeData && bubbleSearchForIgnoreElement(ev.target.nodeData)) {
                    doNotShowMenu = true;
                }

                const targetIsBookmarkOrHighlight = eventTarget?.frameworkComponent === EngineEventTargetFrameworkComponent.READER_VIEW_ANNOTATION;

                if (targetIsBookmarkOrHighlight || cirrusReaderUiStore.getActiveSelection()) {
                    doNotShowMenu = true;
                }

                if (ev.readerDocumentEventState === ReaderDocumentEventState.DEFAULT_PREVENTED) {
                    doNotShowMenu = true;
                }

                // If the reader is in accessibility mode we show a dialog with actions instead of the bottom menu.
                // This is done in the click event handler
                if ($cirrusReaderReadingSystem.isInAccessibilityMode && !cirrusReaderUiStore.getActiveSelection()) {
                    doNotShowMenu = true;
                }
            }

            // If the media player is playing we should not open the menu as clicking the book page also moves the reading
            // position.
            if ($cirrusReaderMediaPlayer.mediaPlayerIsPlaying && !$cirrusReaderPublication.isAudiobook) {
                doNotShowMenu = true;
            }

            // if the pressed button is not the left button (the button id is 1) we skip opening the menu
            if (latestReaderViewPointerDownEventButton !== 1) {
                doNotShowMenu = true;
            }

            const pointerEventDeltas: ICoordinates = {
                x: Math.abs(latestReaderViewPointerDownEventCoords.x - latestReaderViewPointerUpEventCoords.x),
                y: Math.abs(latestReaderViewPointerDownEventCoords.y - latestReaderViewPointerUpEventCoords.y),
            };

            if (doNotShowMenu || pointerEventDeltas.x > 20 || pointerEventDeltas.y > 20) {
                return;
            } else {
                ev.preventDefault();
                if (cirrusReaderUiStore.bottomDrawerOpen) {
                    cirrusReaderUiStore.toggleBottomDrawer(false);
                } else {
                    cirrusReaderUiStore.toggleBottomDrawer(true);
                }
            }
        }


        const _event_pointerUp = async (ev: IPointerEngineEvent) => {
            console.log("CirrusReader._event_pointerUp");
            const popoverState = cirrusReaderUiStore.getPopoverState();
            if (popoverState){
                const selectionRectData = cirrusReaderUiStore.getPopoverState()?.selectionRect;
                if (selectionRectData){
                    const leftToRightTopToBottom: boolean = 
                        popoverState.startX && popoverState.startY && ev.pageX && ev.pageY 
                            ? (popoverState.startX < ev.pageX) || (popoverState.startY < ev.pageY)
                            : true;
                    cirrusReaderUiStore.setPopoverState({
                        open: true,
                        anchorRect: leftToRightTopToBottom ? selectionRectData[selectionRectData.length - 1] : selectionRectData[0],
                        endX: ev.pageX,
                        endY: ev.pageY,
                    });
                }
            }
        }

        const _event_pointerDown = async (ev: IPointerEngineEvent) => {
            console.log("CirrusReader._event_pointerDown");

            cirrusReaderUiStore.setPopoverState(undefined);
            cirrusReaderUiStore.setPopoverState({
                open: false,
                startX: ev.pageX,
                startY: ev.pageY,
            });
        }

        const _event_navigationIntent = async (ev: INavigationIntentEngineEvent) => {
             ev.preventDefault();
            if (!ev.internalNavigation) {
                _event_navigationExternalIntent(ev.locator.toString());
            } else {
                let targetContentPositionData: ICirrusReaderContentPositionData | undefined;

                if(_colibrioReaderPublication && ev.relatedEvent?.target?.contentLocation && useCirrusReaderUtils().isEpubReaderPublication(_colibrioReaderPublication)) {
                    targetContentPositionData = await $cirrusReaderView.fetchContentPositionData(ev.relatedEvent?.target?.contentLocation);
                }

                _event_navigationInternalIntent(ev, targetContentPositionData);
            }
        }

        /**
         * Plugin helper functions
         */

        const _registerWordRangeReadToStats = (wordRange: IntRange): void => {
            console.log("CirrusReader._registerWordRangeReadToStats");

            $cirrusReaderView?.readingStats?.addWordRange(wordRange);
        }

        const focusOnReadingPosition = async (options?: IFocusOnReadingPositionOptions): Promise<void> => {
            if ($cirrusReaderView) {
                return $cirrusReaderView.focusOnReadingPosition(options || {
                    focusOnPageContainer: false,
                    focusOnPageBodyElement: false,
                    focusNearContentLocation: true,
                });
            }
        }

        const _getSelectorFromFragmentSelector = (selectors: string[]): ILocator | null => {
            console.log("CirrusReader._getSelectorFromFragmentSelector");

            if ($cirrusReaderPublication) {
                const selector: ISimpleLocatorData = {
                    sourceUrl: $cirrusReaderPublication.colibrioReaderPublication.getDefaultLocatorUrl(),
                    selectors: selectors
                };
                const selectorFactory = new SelectorFactory([
                    new EpubCfiFragmentSelectorParser(),
                    new PdfFragmentSelectorParser(),
                ]);
                return new LocatorFactory(selectorFactory).parse(selector).locator;
            }
            return null;
        }


        /**
         * Reader functionality/control including navigation
         * Accessed via cirrusReader provider in Nuxt
         */

        const refreshReaderView = (force: boolean = false): void => {
            console.log("CirrusReader.refreshReaderView");

            $cirrusReaderView?.refresh(force);
        }

        const processKeyUpEvent = async (ev: KeyboardEvent) => {
            if (ev.key === 'ArrowRight' || ev.key === 'ArrowLeft' || ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
                ev.preventDefault();
                ev.stopPropagation();
            }

            switch (ev.key){
                case 'ArrowRight':
                    if (!uiBlockingGate.isUiBlocking.value){
                        await next();
                    }
                    break;
                case 'ArrowLeft':
                    if (!uiBlockingGate.isUiBlocking.value){
                        await previous();
                    }
                    break;
                case 'ArrowUp':
                    cirrusReaderUiStore.toggleBottomDrawer(true);
                    break;
                case 'ArrowDown':
                    cirrusReaderUiStore.toggleBottomDrawer(false);
                    break;
            }
        }

        const goTo = async (locator: ISimpleLocatorData | ILocator | string, options?: IReaderViewGotoOptions): Promise<void> => {
            console.log("CirrusReader.goTo");
            if ($cirrusReaderView) {
                let useLocator: string | ISimpleLocatorData | ILocator | null = locator;
                try {
                    await $cirrusReaderView.goTo(locator, options);
                    useLocator = locator;
                } catch (err){
                    console.warn("CirrusReader.goTo: Something went wrong navigating this publication.", err);
                    if (typeof locator === 'string'){
                        useLocator = _getSelectorFromFragmentSelector([locator]);
                        if (useLocator){
                            await $cirrusReaderView.goTo(useLocator, options);
                        }
                    }
                }
            } else {
                console.warn("CirrusReader.goTo: No reader view available.")
            }
        }
    
        const goToStart = async (): Promise<void> => {
            console.log("CirrusReader.goToStart");

            if (!$cirrusReaderPublication?.isAudiobook && $cirrusReaderView) {
                if (!$cirrusReaderView.isAtStart()) {
                    await $cirrusReaderView.goToStart();
                }
            }
        }
    
        const gotoTimelinePosition = async (position: number): Promise<void> => {
            console.log("CirrusReader.gotoTimelinePosition", position);

            if (cirrusReadingProgressionTimeLine) {
                if (!$cirrusReaderPublication?.isAudiobook) {
                    const contentLocation = await cirrusReadingProgressionTimeLine.fetchContentLocation(position);
                    if (contentLocation) {
                        await goTo(contentLocation.getLocator(), {
                            setReadingPositionToVisibleRangeStart: true
                        });
                    } else {
                        console.warn("CirrusReader.gotoTimelinePosition: No content location found for position: " + position);
                    }
                }
            } else {
                console.warn("CirrusReader.gotoTimelinePosition: No timeline available.");
            }
        }
    
        const next = async (): Promise<void> => {
            console.log("CirrusReader.next");

            if (!$cirrusReaderPublication?.isAudiobook && $cirrusReaderView && $cirrusReaderView.canPerformNext()) {
                await $cirrusReaderView.next().catch(console.error);
            }
            await $cirrusReaderView?.getReadingPositionData();
        }
    
        const previous = async (): Promise<void> => {
            console.log("CirrusReader.previous");

            if (!$cirrusReaderPublication?.isAudiobook && $cirrusReaderView && $cirrusReaderView.canPerformPrevious()) {
                await $cirrusReaderView.previous().catch(console.error);
            }
            await $cirrusReaderView?.getReadingPositionData();
        }
        
        const toggleMediaPlayback = async (state: boolean, locator?: ILocatorData): Promise<void> => {
            console.log("CirrusReader.toggleMediaPlayback");

            if (state) {
                try {
                    return await _startMediaPlayback(locator);
                } catch (message) {
                    return console.warn(message);
                }
            } else {
                return new Promise<void>((resolve, _reject) => {
                    _pauseMediaPlayback();
                    return resolve();
                });
            }
        }
        
        const seekForward = (offsetMs: number = 10000) => {
            if ($cirrusReaderMediaPlayer && $cirrusReaderMediaPlayer.playerIsAttachedToView && $cirrusReaderMediaPlayer.colibrioSyncMediaPlayerInstance) {
                $cirrusReaderMediaPlayer.seekToApproximateMs($cirrusReaderMediaPlayer.colibrioSyncMediaPlayerInstance.getApproximateElapsedTimeMs() + offsetMs)
            }
            // TODO: handle audiobook as else statement here
        }

        const seekBackward = (offsetMs: number = 10000) => {
            if ($cirrusReaderMediaPlayer && $cirrusReaderMediaPlayer.playerIsAttachedToView && $cirrusReaderMediaPlayer.colibrioSyncMediaPlayerInstance) {
                $cirrusReaderMediaPlayer.seekToApproximateMs($cirrusReaderMediaPlayer.colibrioSyncMediaPlayerInstance.getApproximateElapsedTimeMs() - offsetMs)
            }
            // TODO: handle audiobook as else statement here
        }

        const seekToNext = () => {
            if ($cirrusReaderMediaPlayer && $cirrusReaderMediaPlayer.playerIsAttachedToView) {
                $cirrusReaderMediaPlayer.seekToNextSegment();
            }
            // TODO: handle audiobook as else statement here
        }

        const seekToPrevious = () => {
            if ($cirrusReaderMediaPlayer && $cirrusReaderMediaPlayer.playerIsAttachedToView) {
                $cirrusReaderMediaPlayer.seekToPreviousSegment();
            }
            // TODO: handle audiobook as else statement here
        }

        const _startMediaPlayback = async (locator?: ILocatorData): Promise<void> => {
            console.log("CirrusReader._startMediaPlayback");
            if ($cirrusReaderMediaPlayer){
                if ($cirrusReaderMediaPlayer.playerIsAttachedToView) {
                    if (locator)  await $cirrusReaderMediaPlayer.seekToLocator(locator);
                    $cirrusReaderMediaPlayer.play();
                } else {
                    console.warn("The CirrusReaderMediaPlayer is not attached to the view. Call attach() on the player first.");
                }
            } else {
                console.warn("The CirrusReaderMediaPlayer is not available");
            }
        }
    
        const _pauseMediaPlayback = (): void => {
            console.log("CirrusReader._pauseMediaPlayback");

            if ($cirrusReaderMediaPlayer && $cirrusReaderMediaPlayer.playerIsAttachedToView) {
                $cirrusReaderMediaPlayer.pause();
            }
        }


        /**
         * Media Playback Functions
         */
        const _createSyncMediaPlayer = async (
            syncMediaOptions?: ICirrusReaderSyncMediaOptions
        ): Promise<void> => {
            console.log("CirrusReader._createSyncMediaPlayer");

            $cirrusReaderMediaPlayer.initMediaPlayer(syncMediaOptions);
    
            await $cirrusReaderMediaPlayer.create().catch(console.warn);
    
            $cirrusReaderMediaPlayer.setEscapabableContentBlockTypes([
                ContainerContentBlockType.ASIDE,
                ContainerContentBlockType.TABLE,
                ContainerContentBlockType.TABLE_CELL,
                ContainerContentBlockType.TABLE_ROW,
                ContainerContentBlockType.ORDERED_LIST,
                ContainerContentBlockType.UNORDERED_LIST,
                ContainerContentBlockType.DESCRIPTION_LIST,
                ContainerContentBlockType.LIST_ITEM,
                ContainerContentBlockType.MATHML,
                ContainerContentBlockType.MATHML_TABLE,
                ContainerContentBlockType.PREFORMATTED,
    
            ]);
        }

        const toggleMediaPlayer = async (activate: boolean, options: ICirrusReaderSyncMediaOptions): Promise<void> => {
            console.log("CirrusReader.toggleMediaPlayer");

            if ($cirrusReaderView) {
                if (activate) {
                    if (!$cirrusReaderMediaPlayer.playerIsAttachedToView) {
                        await _createSyncMediaPlayer(options);
                    }
                    $cirrusReaderMediaPlayer.toggle(true);
                } else {
                    if ($cirrusReaderMediaPlayer) {
                        $cirrusReaderMediaPlayer.pause();
                        $cirrusReaderMediaPlayer.toggle(false);
                    }
                }
            } else {
                console.warn('CirrusReader.toggleMediaPlaye: No $cirrusReaderView instance found. Is this an audiobook?');
            }
        }


        return {
            provide: {
                cirrusReader: {
                    initReader,
                    loadPublicationFromUrl,
                    initaliseWebSpeechPlayer,
                    refreshReaderView,
                    navigate,
                    goTo,
                    goToStart,
                    gotoTimelinePosition,
                    next,
                    previous,
                    seekForward,
                    seekBackward,
                    seekToNext,
                    seekToPrevious,
                    toggleMediaPlayer,
                    toggleMediaPlayback,
                    speakText,
                    focusOnReadingPosition,
                    processKeyUpEvent,
                    get shouldFocusOnReadingPosition(): boolean {
                        return _shouldFocusOnReadingPosition;
                    },
                    set shouldFocusOnReadingPosition(value: boolean) {
                        _shouldFocusOnReadingPosition = value;
                    }
                },
            },
        }
    },
    env: {
      // Set this value to `false` if you don't want the plugin to run when rendering server-only or island components.
      islands: true
    }
})